import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import ComingSoon from './tools/ComingSoon';
import { getToolBySlug } from './tools/registry';
import { getToolSeo, getDashboardSeo, getHomeSeo } from './tools/seo';
import {
  applyJsonLd,
  buildBreadcrumb,
  buildFaqPage,
  buildHowTo,
  buildSoftwareApplication,
} from './lib/jsonLd';
import { resolveSiteContext, getCanonicalUrl } from './lib/subdomain';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { EzoicProvider } from '@ezoic/react-sdk';
import AdProvider from './ads/AdProvider';
import ClickBankTidTracker from './components/ClickBankTidTracker';
import AffiliateLandingPage from './pages/AffiliateLandingPage';
import './App.css';
import lazyWithRetry from './lib/lazyWithRetry';
import { loadSiteContentPage } from './lib/siteContent';

const lazy = lazyWithRetry;

const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const UserLogin = lazy(() => import('./pages/UserLogin'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const AffiliateDisclosure = lazy(() => import('./pages/AffiliateDisclosure'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const DownloadReady = lazy(() => import('./pages/DownloadReady'));

// Route-level code-splitting. Every tool page pulls in a heavy PDF/OCR/Office
// library (pdfjs-dist, pdf-lib, docx, tesseract.js, mammoth, xlsx, jszip,
// html2canvas), so importing them statically bloats the initial bundle to
// ~900 KB gzipped and hurts Core Web Vitals. React.lazy() emits one chunk
// per tool, so visitors to /tools/pdfmerger only download pdfmerger code.
// The surrounding <Suspense> boundary covers both the <Routes> tree and the
// SubdomainSwitch tool-page branch.
const ToolsDashboard = lazy(() => import('./pages/ToolsDashboard'));
const PDFMergerPage = lazy(() => import('./tools/pdfmerger/PDFMergerPage'));
const PDFCompressPage = lazy(() => import('./tools/pdfcompress/PDFCompressPage'));
const PDFSplitterPage = lazy(() => import('./tools/pdfsplitter/PDFSplitterPage'));
const JPGToPDFPage = lazy(() => import('./tools/jpgtopdf/JPGToPDFPage'));
const ImageToPDFToolPage = lazy(() => import('./tools/imagetopdf/ImageToPDFToolPage'));
const PNGToPDFPage = lazy(() => import('./tools/pngtopdf/PNGToPDFPage'));
const BMPToPDFPage = lazy(() => import('./tools/bmptopdf/BMPToPDFPage'));
const WebPToPDFPage = lazy(() => import('./tools/webptopdf/WebPToPDFPage'));
const TXTToPDFPage = lazy(() => import('./tools/txttopdf/TXTToPDFPage'));
const HTMLToPDFPage = lazy(() => import('./tools/htmltopdf/HTMLToPDFPage'));
const PDFToJPGPage = lazy(() => import('./tools/pdftojpg/PDFToJPGPage'));
const PDFToPNGPage = lazy(() => import('./tools/pdftopng/PDFToPNGPage'));
const PDFToWebPPage = lazy(() => import('./tools/pdftowebp/PDFToWebPPage'));
const PDFToTXTPage = lazy(() => import('./tools/pdftotxt/PDFToTXTPage'));
const PDFToHTMLPage = lazy(() => import('./tools/pdftohtml/PDFToHTMLPage'));
const PDFToWordPage = lazy(() => import('./tools/pdftoword/PDFToWordPage'));
const WordToPDFPage = lazy(() => import('./tools/wordtopdf/WordToPDFPage'));
const ExcelToPDFPage = lazy(() => import('./tools/exceltopdf/ExcelToPDFPage'));
const PDFRotatorPage = lazy(() => import('./tools/pdfrotator/PDFRotatorPage'));
const PDFWatermarkPage = lazy(() => import('./tools/pdfwatermark/PDFWatermarkPage'));

// Non-tool heavy surfaces — admin console, user dashboard, library/products,
// and checkout flows — also get their own chunks so the public marketing
// pages stay light.
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const PDFLibraryPage = lazy(() => import('./pages/PDFLibraryPage'));
const PDFDetailsPage = lazy(() => import('./pages/PDFDetailsPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetails = lazy(() => import('./pages/ProductDetails'));
const SideHustleValidationLab = lazy(() => import('./pages/SideHustleValidationLab'));
const SideHustleRealityCheck = lazy(() => import('./pages/SideHustleRealityCheck'));
const CheckoutFunnelFlow = lazy(() => import('./pages/CheckoutFunnelFlow'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('./pages/CheckoutCancel'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('./pages/SubscriptionCancel'));
const PurchaseConfirmation = lazy(() => import('./pages/PurchaseConfirmation'));
const SubscriptionConfirmation = lazy(() => import('./pages/SubscriptionConfirmation'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Dispatch table: tool slug -> page component. Tools not listed here render
// the ComingSoon placeholder. Add an entry when shipping a new tool.
const TOOL_COMPONENTS = {
  pdfmerger: PDFMergerPage,
  pdfcompress: PDFCompressPage,
  pdfsplitter: PDFSplitterPage,
  jpgtopdf: JPGToPDFPage,
  imagetopdf: ImageToPDFToolPage,
  pngtopdf: PNGToPDFPage,
  bmptopdf: BMPToPDFPage,
  webptopdf: WebPToPDFPage,
  txttopdf: TXTToPDFPage,
  htmltopdf: HTMLToPDFPage,
  pdftojpg: PDFToJPGPage,
  pdftopng: PDFToPNGPage,
  pdftowebp: PDFToWebPPage,
  pdftotxt: PDFToTXTPage,
  pdftohtml: PDFToHTMLPage,
  pdftoword: PDFToWordPage,
  wordtopdf: WordToPDFPage,
  exceltopdf: ExcelToPDFPage,
  pdfrotator: PDFRotatorPage,
  pdfwatermark: PDFWatermarkPage,
};

function renderToolPage(tool) {
  if (!tool) return null;
  const Component = TOOL_COMPONENTS[tool.slug];
  if (Component) return <Component />;
  return <ComingSoon tool={tool} />;
}

function setMetaTag(selector, attrs) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs || {}).forEach(([k, v]) => {
      if (k === 'content') return;
      el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  if (attrs?.content != null) el.setAttribute('content', String(attrs.content));
}

function setCanonical(href) {
  if (typeof document === 'undefined' || !href) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
  // og:url should match the canonical to avoid social-card confusion.
  let og = document.head.querySelector('meta[property="og:url"]');
  if (!og) {
    og = document.createElement('meta');
    og.setAttribute('property', 'og:url');
    document.head.appendChild(og);
  }
  og.setAttribute('content', href);
}

const DEFAULT_SOCIAL_IMAGE = 'https://tinypdfs.com/assets/social/tinypdfs-default-social.png';

function applySeo({ title, description, canonical, jsonLd, keywords, image, imageAlt }) {
  if (typeof document === 'undefined') return;
  if (title) document.title = title;
  if (description) {
    setMetaTag('meta[name="description"]', { name: 'description', content: description });
    setMetaTag('meta[property="og:title"]', { property: 'og:title', content: title || document.title });
    setMetaTag('meta[property="og:description"]', { property: 'og:description', content: description });
    setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title || document.title });
    setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
  }
  if (keywords && keywords.length) {
    const value = Array.isArray(keywords) ? keywords.join(', ') : String(keywords);
    setMetaTag('meta[name="keywords"]', { name: 'keywords', content: value });
  }
  const socialImage = image || DEFAULT_SOCIAL_IMAGE;
  setMetaTag('meta[property="og:image"]', { property: 'og:image', content: socialImage });
  setMetaTag('meta[property="og:image:alt"]', { property: 'og:image:alt', content: imageAlt || 'TinyPDFs - Small PDFs. Big Results.' });
  setMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: socialImage });
  setMetaTag('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: imageAlt || 'TinyPDFs - Small PDFs. Big Results.' });
  if (canonical) setCanonical(canonical);
  // jsonLd: pass `null` to clear, `undefined` to leave untouched.
  if (jsonLd !== undefined) applyJsonLd(jsonLd || []);
}

// Compose the full schema.org graph for a tool page: SoftwareApplication +
// BreadcrumbList + FAQPage + HowTo. Breadcrumb mirrors the visible path.
function toolJsonLd(seo) {
  if (!seo) return [];
  return [
    buildSoftwareApplication({
      name: seo.name,
      url: seo.url,
      description: seo.description,
      keywords: seo.keywords,
    }),
    buildBreadcrumb([
      { name: 'Home', url: '/' },
      { name: 'PDF Tools', url: '/tools' },
      { name: seo.name, url: seo.url },
    ]),
    buildFaqPage(seo.faqs),
    seo.howTo ? buildHowTo(seo.howTo) : null,
  ];
}

function dashboardJsonLd(seo) {
  return [
    buildBreadcrumb([
      { name: 'Home', url: '/' },
      { name: 'PDF Tools', url: '/tools' },
    ]),
    buildSoftwareApplication({
      name: 'TinyPDFs PDF Tools',
      url: seo.url,
      description: seo.description,
      keywords: seo.keywords,
    }),
  ];
}

function LegalModal({ title, children }) {
  const navigate = useNavigate();

  return (
    <Dialog open={true} onOpenChange={() => navigate(-1)}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Resolves a tool page from a URL param (/tools/:slug). Unknown slugs redirect
// to the dashboard. Tools without a dedicated component render ComingSoon.
function ToolBySlugRoute() {
  const { slug } = useParams();
  const tool = getToolBySlug(slug);
  if (!tool) return <Navigate to="/tools" replace />;
  return renderToolPage(tool);
}

function SitemapRedirect() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.location.replace(`${BACKEND_URL}/sitemap.xml`);
  }, []);

  return null;
}

function AppRoutes() {
  const location = useLocation();
  const state = location.state;
  const backgroundLocation = state && state.backgroundLocation;

  const [siteSeo, setSiteSeo] = useState({ title: 'TinyPDFs', description: '' });

  useEffect(() => {
    const loadSiteSeo = async () => {
      try {
        const rows = await loadSiteContentPage('site_seo');
        const title = rows.find((r) => r?.key === 'title')?.value;
        const description = rows.find((r) => r?.key === 'description')?.value;
        setSiteSeo({
          title: (title || 'TinyPDFs').toString(),
          description: (description || '').toString(),
        });
      } catch {
        setSiteSeo((prev) => prev || { title: 'TinyPDFs', description: '' });
      }
    };
    loadSiteSeo();
  }, []);

  const seoForPath = useMemo(() => {
    const baseTitle = siteSeo?.title || 'TinyPDFs';
    const baseDesc = siteSeo?.description || 'TinyPDFs provides concise, two-page PDFs and digital products like templates and bundles, offering actionable insights for creators, entrepreneurs, and lifelong learners.';

    const path = (location.pathname || '/').toLowerCase();

    if (path === '/' || path === '') return { title: baseTitle, description: baseDesc };
    if (path.startsWith('/pdfs/') || path.startsWith('/library/')) return { title: `TinyPDF Guide | ${baseTitle}`, description: baseDesc };
    if (path.startsWith('/pdfs') || path.startsWith('/library')) return { title: `Free PDF Library | ${baseTitle}`, description: baseDesc };
    if (path.startsWith('/products')) return { title: `Digital Products | ${baseTitle}`, description: baseDesc };
    if (path.startsWith('/premium')) return { title: `Premium | ${baseTitle}`, description: baseDesc };
    if (path.startsWith('/about')) return {
      title: `About & Editorial Standards | ${baseTitle}`,
      description: 'Meet the TinyPDFs Editorial Team and read the sourcing, AI-assistance, correction, and publication standards behind every practical guide.',
    };
    if (path.startsWith('/privacy-policy')) return { title: `Privacy Policy | ${baseTitle}`, description: baseDesc };
    if (path.startsWith('/terms-of-service')) return { title: `Terms of Service | ${baseTitle}`, description: baseDesc };
    if (path.startsWith('/affiliate-disclosure')) return { title: `Affiliate Disclosure | ${baseTitle}`, description: baseDesc };
    if (path.startsWith('/admin')) return { title: `Admin | ${baseTitle}`, description: baseDesc };
    return { title: baseTitle, description: baseDesc };
  }, [location.pathname, siteSeo]);

  useEffect(() => {
    const canonical = getCanonicalUrl(location.pathname);
    const path = (location.pathname || '/').toLowerCase();

    if (path === '/side-hustle-validation-lab' || path === '/side-hustle-validation-lab/') {
      const description = 'A 14-day validation app and complete digital toolkit for testing a side-hustle idea with customer evidence, a priced pilot, and a clear next decision.';
      applySeo({
        title: 'Side-Hustle Validation Lab | Test Your Idea in 14 Days',
        description,
        keywords: ['side-hustle validation', 'business idea validation', 'customer interview workbook', 'validation app', 'paid pilot'],
        canonical: 'https://tinypdfs.com/side-hustle-validation-lab',
        jsonLd: [
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Side-Hustle Validation Lab', url: '/side-hustle-validation-lab' },
          ]),
        ],
      });
      return;
    }

    // Rich SEO for the tools surfaces — overrides the generic map above.
    if (path === '/tools' || path === '/tools/') {
      const d = getDashboardSeo();
      applySeo({
        title: d.title,
        description: d.description,
        keywords: d.keywords,
        canonical: getCanonicalUrl('/tools'),
        jsonLd: dashboardJsonLd(d),
      });
      return;
    }
    const toolMatch = path.match(/^\/tools\/([a-z0-9-]+)\/?$/);
    if (toolMatch) {
      const toolSeo = getToolSeo(toolMatch[1]);
      if (toolSeo) {
        applySeo({
          title: toolSeo.title,
          description: toolSeo.description,
          keywords: toolSeo.keywords,
          canonical: getCanonicalUrl(`/tools/${toolSeo.slug}`),
          jsonLd: toolJsonLd(toolSeo),
        });
        return;
      }
    }

    // Homepage gets its own targeted pack.
    if (path === '/' || path === '') {
      const h = getHomeSeo();
      applySeo({
        title: h.title,
        description: h.description,
        keywords: h.keywords,
        canonical,
        jsonLd: [
          buildBreadcrumb([{ name: 'Home', url: '/' }]),
        ],
      });
      return;
    }

    // Detail pages load their own record and then apply the precise title,
    // description, canonical URL, and structured data. Do not overwrite that
    // metadata when the site-wide settings request completes.
    if (/^\/(?:pdfs|library|products)\/[^/]+\/?$/.test(path)) return;

    // Everything else: generic SEO, clear any page-specific JSON-LD so we
    // don't carry stale FAQ/HowTo markup across routes.
    applySeo({ ...seoForPath, canonical, jsonLd: [] });
  }, [seoForPath, location.pathname]);

  return (
    <>
      <Routes location={backgroundLocation || location}>
        <Route path="/sitemap.xml" element={<SitemapRedirect />} />
        <Route path="/" element={<Home />} />
        <Route path="/pdfs" element={<PDFLibraryPage />} />
        <Route path="/pdfs/:pdfId" element={<PDFDetailsPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:slug" element={<ProductDetails />} />
        <Route path="/side-hustle-validation-lab" element={<SideHustleValidationLab />} />
        <Route path="/side-hustle-reality-check" element={<SideHustleRealityCheck />} />
        <Route path="/login" element={<UserLogin />} />
        <Route path="/dashboard" element={<UserDashboard />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
        <Route path="/offers/:slug" element={<AffiliateLandingPage />} />
        <Route path="/download/ready" element={<DownloadReady />} />
        <Route path="/confirm/purchase" element={<PurchaseConfirmation />} />
        <Route path="/confirm/subscription" element={<SubscriptionConfirmation />} />
        <Route path="/checkout/funnel" element={<CheckoutFunnelFlow />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel" element={<CheckoutCancel />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
        <Route path="/admin/*" element={<AdminDashboard />} />
        {/* Path-based fallbacks so tools work on localhost/preview deploys
            where wildcard subdomains are not available. In production the
            subdomain switch in <App> takes over before these routes render. */}
        <Route path="/tools" element={<ToolsDashboard />} />
        <Route path="/tools/:slug" element={<ToolBySlugRoute />} />
      </Routes>

      {backgroundLocation ? (
        <Routes>
          <Route
            path="/privacy-policy"
            element={
              <LegalModal title="Privacy Policy">
                <PrivacyPolicy modal={true} />
              </LegalModal>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <LegalModal title="Terms of Service">
                <TermsOfService modal={true} />
              </LegalModal>
            }
          />
          <Route
            path="/affiliate-disclosure"
            element={
              <LegalModal title="Affiliate Disclosure">
                <AffiliateDisclosure modal={true} />
              </LegalModal>
            }
          />
        </Routes>
      ) : null}
    </>
  );
}

// Applies title/description/canonical when the site is served from a legacy
// tool or dashboard subdomain (where AppRoutes is bypassed and the useEffect
// inside it never fires). Canonical always points at the apex path, so any
// stray subdomain request is consolidated back to tinypdfs.com for SEO.
function useSubdomainSeo(ctx) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (ctx.kind === 'dashboard') {
      const d = getDashboardSeo();
      applySeo({
        title: d.title,
        description: d.description,
        keywords: d.keywords,
        canonical: getCanonicalUrl('/tools'),
        jsonLd: dashboardJsonLd(d),
      });
      return;
    }

    if (ctx.kind === 'tool') {
      const toolSeo = getToolSeo(ctx.slug);
      if (toolSeo) {
        applySeo({
          title: toolSeo.title,
          description: toolSeo.description,
          keywords: toolSeo.keywords,
          canonical: getCanonicalUrl(`/tools/${toolSeo.slug}`),
          jsonLd: toolJsonLd(toolSeo),
        });
      }
    }
  }, [ctx.kind, ctx.slug]);
}

// Renders the correct tree based on the current hostname:
//   tools.tinypdfs.com      -> ToolsDashboard
//   <slug>.tinypdfs.com     -> tool page (ComingSoon for now)
//   anything else           -> normal site (AppRoutes)
// Falls back to the main site on localhost unless ?host=/?tool= overrides apply.
function SubdomainSwitch() {
  const ctx = resolveSiteContext();
  useSubdomainSeo(ctx);

  if (ctx.kind === 'dashboard') {
    return <ToolsDashboard />;
  }

  if (ctx.kind === 'tool') {
    const tool = getToolBySlug(ctx.slug);
    if (tool) return renderToolPage(tool);
  }

  return <AppRoutes />;
}

// Minimal, unbranded fallback shown while a lazy-loaded route chunk is
// fetching. Kept deliberately tiny so it costs nothing in the initial bundle
// and doesn't flash brand chrome that the real page is about to render.
function RouteSuspenseFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: '3px solid rgba(47, 138, 197, 0.2)',
          borderTopColor: 'rgba(47, 138, 197, 0.9)',
          animation: 'tinypdfs-spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes tinypdfs-spin { to { transform: rotate(360deg); } }`}</style>
      <span style={{ position: 'absolute', left: -9999 }}>Loading…</span>
    </div>
  );
}

function App() {
  return (
    <EzoicProvider analyticsUrl="https://ezoicanalytics.com/analytics.js">
      <BrowserRouter>
        <AdProvider>
          <ClickBankTidTracker />
          <Suspense fallback={<RouteSuspenseFallback />}>
            <SubdomainSwitch />
          </Suspense>
        </AdProvider>
      </BrowserRouter>
    </EzoicProvider>
  );
}

export default App;
